// WHERE YOU WERE ON A PAGE comes back with the page. The browser restores the
// DOCUMENT's scroll per history entry; a stage scrolls an element, which the
// browser knows nothing about. So each history entry the stage writes carries an
// id (`stageEntry`), the scroller's offset is kept against it (in memory, and in
// sessionStorage so a refresh finds it too), and:
//
//   · a push (a new place)        opens at the top;
//   · Back / Forward              restore the offset that entry had;
//   · a refresh                   restores the offset of the entry it reloads.
//
// Content often arrives after the place does (a project's subtree, a panel's
// data), so a restore keeps trying while the page grows, until it fits, the
// person scrolls themselves, or a short deadline passes.

// Back and Forward, as they happen: a page that arrives right after one is a
// return, and opens where it was (a surface whose places are separate pages —
// a post and its index — has no history state of its own to ask).
let lastPop = -1e9;
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => { lastPop = performance.now(); });
}
/** did a Back / Forward just happen (within `ms`)? */
export function poppedRecently(ms = 1500): boolean {
  return typeof performance !== "undefined" && performance.now() - lastPop < ms;
}
/** the memory key of the address the page stands on (one offset per address). */
export function addressKey(): string {
  return typeof window === "undefined" ? "url:" : `url:${window.location.pathname}${window.location.search}`;
}

const KEY = "stage:scroll:";
const INDEX = "stage:scroll";
const CAP = 200;
const mem = new Map<string, number>();
let seq = 0;

export function newEntryId(): string {
  seq += 1;
  return `${Date.now().toString(36)}${seq.toString(36)}`;
}

export function entryIdOf(state: unknown): string | null {
  const id = (state as { stageEntry?: unknown } | null)?.stageEntry;
  return typeof id === "string" && id ? id : null;
}

export function withEntryId(state: unknown, id: string): Record<string, unknown> {
  const base = state && typeof state === "object" ? (state as Record<string, unknown>) : {};
  return { ...base, stageEntry: id };
}

let persistTimer: ReturnType<typeof setTimeout> | null = null;
const dirty = new Set<string>();

/** keep `top` for entry `id`: in memory at once (Back reads it), on disk a beat later (a refresh does). */
export function saveScroll(id: string, top: number): void {
  const v = Math.max(0, Math.round(top));
  mem.set(id, v);
  dirty.add(id);
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    try {
      // a bounded memory: the most recent places only (one key per push otherwise grows forever)
      const order: string[] = JSON.parse(sessionStorage.getItem(INDEX) ?? "[]");
      for (const k of dirty) {
        sessionStorage.setItem(KEY + k, String(mem.get(k) ?? 0));
        const at = order.indexOf(k);
        if (at >= 0) order.splice(at, 1);
        order.push(k);
      }
      while (order.length > CAP) sessionStorage.removeItem(KEY + order.shift());
      sessionStorage.setItem(INDEX, JSON.stringify(order));
    } catch {
      /* private mode: memory alone is enough for Back */
    }
    dirty.clear();
  }, 160);
}

export function readScroll(id: string): number | null {
  if (mem.has(id)) return mem.get(id)!;
  try {
    const raw = sessionStorage.getItem(KEY + id);
    if (raw == null) return null;
    const v = Number(raw);
    return Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Put `el` back at `top`, waiting (≤ maxMs) for content tall enough to hold it.
 * A wheel, a touch or a key from the person ends the attempt: their scroll wins.
 * Returns a cancel.
 */
export function restoreScroll(el: HTMLElement, top: number, maxMs = 1500): () => void {
  if (top <= 0) {
    el.scrollTop = 0;
    return () => {};
  }
  let done = false;
  let wrote = -1;
  const started = performance.now();
  const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => attempt()) : null;
  // any scroll this didn't write is the person's (a relayed wheel or key, a
  // drag on the scrollbar): theirs wins
  const onScroll = () => { if (wrote >= 0 && Math.abs(el.scrollTop - wrote) > 2) stop(); };
  const stop = () => {
    if (done) return;
    done = true;
    ro?.disconnect();
    el.removeEventListener("wheel", stop);
    el.removeEventListener("touchstart", stop);
    el.removeEventListener("keydown", stop);
    el.removeEventListener("pointerdown", stop);
    el.removeEventListener("scroll", onScroll);
    window.clearTimeout(timer);
  };
  const attempt = () => {
    if (done) return;
    const max = el.scrollHeight - el.clientHeight;
    el.scrollTop = Math.min(top, Math.max(0, max));
    wrote = el.scrollTop;
    if (max >= top || performance.now() - started > maxMs) stop();
  };
  const timer = window.setTimeout(stop, maxMs);
  el.addEventListener("wheel", stop, { passive: true });
  el.addEventListener("touchstart", stop, { passive: true });
  el.addEventListener("keydown", stop);
  el.addEventListener("pointerdown", stop);
  el.addEventListener("scroll", onScroll, { passive: true });
  attempt();
  if (!done && ro) {
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
  }
  return stop;
}
